import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Plus, Minus, Trash2, Pill, Package, AlertCircle, Search, X, Loader2 } from 'lucide-react';
import { isFeatureEnabled, FEATURE_KEYS } from '../../redux/featureSlice';
import {
    getDoctorMedicines,
    getPrescriptionsByAppointment,
    createPrescription,
    updatePrescription,
    deletePrescription,
} from '../../redux/medicineSlice';

const MAX_COUNT = 10;
const MAX_DURATION = 365;
const DEFAULT_DURATION = 7;

const Counter = ({ value, onChange, disabled }) => (
    <div className="flex items-center justify-center gap-1">
        <button
            type="button"
            data-testid="patient-prescription-counter-decrement-button"
            disabled={disabled || value <= 0}
            onClick={() => onChange(Math.max(0, value - 1))}
            className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
            <Minus size={12} />
        </button>
        <span className="w-6 text-center text-sm font-medium text-gray-700">{value}</span>
        <button
            type="button"
            data-testid="patient-prescription-counter-increment-button"
            disabled={disabled || value >= MAX_COUNT}
            onClick={() => onChange(Math.min(MAX_COUNT, value + 1))}
            className="w-6 h-6 rounded-full border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
        >
            <Plus size={12} />
        </button>
    </div>
);

function PrescriptionSection({ appointment, readOnly = false }) {
    const { t } = useTranslation(['prescriptionSection']);
    const dispatch = useDispatch();

    const doctorId = appointment?.doctorId;
    const patientId = appointment?.patientId;
    const appointmentId = appointment?.appointmentId;

    const { medicines, prescriptions, prescriptionsLoading } = useSelector(
        (state) => state.medicine
    );
    // Super-admin Medicines feature flag (Settings → Doctor → Medicines).
    const medicinesEnabled = useSelector((state) => isFeatureEnabled(state, FEATURE_KEYS.medicines));

    const [addMode, setAddMode] = useState(false);
    const [addSearch, setAddSearch] = useState('');
    const [adding, setAdding] = useState(false);
    const [removingAll, setRemovingAll] = useState(false);
    const [dropdownPos, setDropdownPos] = useState(null);
    // All row-level edits (counter +/-, food timing, S/T toggle, days, notes)
    // buffer here keyed by prescriptionId — the API is only hit when the user
    // clicks Save Changes. Shape: `{ [prescriptionId]: { field: value, … } }`.
    // Add / Remove still go straight to the API since they're structural.
    const [drafts, setDrafts] = useState({});
    const [saving, setSaving] = useState(false);
    // Days / notes inputs additionally use these short-lived "typing buffers"
    // so the user can type a transient value (e.g. "1" → "12" while typing
    // "twelve") without the drafts map flickering between every keystroke.
    // On blur the typing buffer flows into `drafts`.
    const [daysDraft, setDaysDraft] = useState({});
    const [notesDraft, setNotesDraft] = useState({});
    const pickerRef = useRef(null);
    const inputRef = useRef(null);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (appointmentId) {
            dispatch(getPrescriptionsByAppointment(appointmentId));
        }
    }, [appointmentId, dispatch]);

    // Fetch the doctor's master once so the inline picker has data ready.
    useEffect(() => {
        if (doctorId) {
            dispatch(getDoctorMedicines({ doctorId }));
        }
    }, [doctorId, dispatch]);

    // Close the inline picker when clicking outside both the input wrapper and
    // the portal-rendered dropdown.
    useEffect(() => {
        if (!addMode) return undefined;
        const handler = (e) => {
            const inInput = pickerRef.current && pickerRef.current.contains(e.target);
            const inDropdown = dropdownRef.current && dropdownRef.current.contains(e.target);
            if (!inInput && !inDropdown) {
                setAddMode(false);
                setAddSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [addMode]);

    useEffect(() => {
        if (addMode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [addMode]);

    // Position the portal dropdown above the input, recompute on scroll / resize
    // so the dropdown follows the input if the page scrolls.
    useLayoutEffect(() => {
        if (!addMode) {
            setDropdownPos(null);
            return undefined;
        }
        const update = () => {
            const el = inputRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const margin = 8;
            const spaceAbove = rect.top - margin;
            const spaceBelow = window.innerHeight - rect.bottom - margin;
            // Open upward if there's more room above, else downward.
            const openUp = spaceAbove >= spaceBelow;
            const maxHeight = Math.min(256, openUp ? spaceAbove : spaceBelow);
            setDropdownPos({
                left: rect.left,
                width: rect.width,
                openUp,
                top: openUp ? undefined : rect.bottom + 4,
                bottom: openUp ? window.innerHeight - rect.top + 4 : undefined,
                maxHeight,
            });
        };
        update();
        window.addEventListener('scroll', update, true);
        window.addEventListener('resize', update);
        return () => {
            window.removeEventListener('scroll', update, true);
            window.removeEventListener('resize', update);
        };
    }, [addMode, addSearch]);

    const alreadyAddedIds = useMemo(
        () => new Set(prescriptions.map((p) => p.medicineId).filter(Boolean)),
        [prescriptions]
    );

    const filteredMaster = useMemo(() => {
        const available = medicines.filter((m) => !alreadyAddedIds.has(m.medicineId));
        if (!addSearch.trim()) return available;
        const q = addSearch.trim().toLowerCase();
        return available.filter(
            (m) =>
                (m.name || '').toLowerCase().includes(q) ||
                (m.brand || '').toLowerCase().includes(q) ||
                (m.purpose || '').toLowerCase().includes(q)
        );
    }, [medicines, addSearch, alreadyAddedIds]);

    const handlePick = async (item) => {
        if (adding) return;
        setAdding(true);
        const payload = {
            appointmentId,
            doctorId,
            patientId,
            medicineId: item.medicineId,
            medicineName: item.name,
            brand: item.brand || null,
            dose: item.dose || null,
            type: item.type || 'medicine',
            isImportant: !!item.isImportant,
            morningCount: 0,
            afternoonCount: 0,
            eveningCount: 0,
            nightCount: 0,
            foodTiming: 'AF',
            durationDays: DEFAULT_DURATION,
            entryMode: 'S',
            notes: null,
        };
        try {
            const res = await dispatch(createPrescription(payload));
            if (createPrescription.fulfilled.match(res)) {
                toast.success(t('prescriptionSection.added', 'Medicine added'));
                setAddMode(false);
                setAddSearch('');
            } else {
                toast.error(t('prescriptionSection.add_failed', 'Failed to add medicine'));
            }
        } finally {
            setAdding(false);
        }
    };

    // Stage a field change in the local drafts map (no API call). The user
    // flushes everything in one shot via the Save Changes button below the
    // table.
    const setDraft = (row, patch) => {
        setDrafts((prev) => {
            const existing = prev[row.prescriptionId] || {};
            const next = { ...existing, ...patch };
            return { ...prev, [row.prescriptionId]: next };
        });
    };

    // Read what a field would be if the user saved right now: the drafted
    // override if set, otherwise the persisted value on the row.
    const valueOf = (row, field) => {
        const d = drafts[row.prescriptionId];
        return d && Object.prototype.hasOwnProperty.call(d, field) ? d[field] : row[field];
    };

    // Number of rows with at least one pending change — drives the Save /
    // Discard button enabled state and the dirty count badge.
    const dirtyCount = Object.keys(drafts).length;

    // Drop drafts for rows that no longer exist (deleted, refreshed away) so
    // dirtyCount doesn't lie. Compares draft IDs against the current
    // prescriptions list every time the list changes.
    useEffect(() => {
        if (Object.keys(drafts).length === 0) return;
        const liveIds = new Set(prescriptions.map((p) => p.prescriptionId));
        const stale = Object.keys(drafts).filter((id) => !liveIds.has(id));
        if (stale.length === 0) return;
        setDrafts((prev) => {
            const next = { ...prev };
            stale.forEach((id) => { delete next[id]; });
            return next;
        });
    }, [prescriptions, drafts]);

    const handleSaveAll = async () => {
        if (saving || dirtyCount === 0) return;
        setSaving(true);
        const entries = Object.entries(drafts);
        try {
            const results = await Promise.all(
                entries.map(([prescriptionId, payload]) =>
                    dispatch(updatePrescription({ prescriptionId, payload }))
                )
            );
            const failedIds = new Set();
            results.forEach((r, idx) => {
                if (!updatePrescription.fulfilled.match(r)) failedIds.add(entries[idx][0]);
            });
            if (failedIds.size === 0) {
                toast.success(t('prescriptionSection.save_success', 'Changes saved'));
                setDrafts({});
            } else if (failedIds.size === results.length) {
                toast.error(t('prescriptionSection.save_failed', 'Failed to save changes'));
            } else {
                toast.warn(t('prescriptionSection.save_partial', '{{n}} change(s) could not be saved', { n: failedIds.size }));
                // Keep only the failed ones so the user can retry.
                setDrafts((prev) => {
                    const next = {};
                    Object.entries(prev).forEach(([id, payload]) => {
                        if (failedIds.has(id)) next[id] = payload;
                    });
                    return next;
                });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDiscardAll = () => {
        if (saving || dirtyCount === 0) return;
        setDrafts({});
        setDaysDraft({});
        setNotesDraft({});
    };

    const handleRemove = async (row) => {
        const ok = await Swal.fire({
            title: t('prescriptionSection.confirm_remove_title', 'Remove medicine?'),
            text: t('prescriptionSection.confirm_remove_text', 'This will remove "{{name}}" from this prescription.', { name: row.medicineName }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('prescriptionSection.remove_confirm', 'Yes, remove'),
            cancelButtonText: t('prescriptionSection.cancel', 'Cancel'),
        });
        if (!ok.isConfirmed) return;
        const res = await dispatch(deletePrescription(row.prescriptionId));
        if (deletePrescription.fulfilled.match(res)) {
            toast.success(t('prescriptionSection.removed', 'Medicine removed'));
        } else {
            toast.error(t('prescriptionSection.remove_failed', 'Remove failed'));
        }
    };

    const handleRemoveAll = async () => {
        if (!prescriptions.length || removingAll) return;
        const ok = await Swal.fire({
            title: t('prescriptionSection.confirm_remove_all_title', 'Remove all medicines?'),
            text: t('prescriptionSection.confirm_remove_all_text', 'This will clear every medicine from this prescription.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#6b7280',
            confirmButtonText: t('prescriptionSection.remove_all_confirm', 'Yes, remove all'),
            cancelButtonText: t('prescriptionSection.cancel', 'Cancel'),
        });
        if (!ok.isConfirmed) return;

        setRemovingAll(true);
        try {
            const results = await Promise.all(
                prescriptions.map((p) => dispatch(deletePrescription(p.prescriptionId)))
            );
            const failed = results.filter((r) => !deletePrescription.fulfilled.match(r)).length;
            if (failed === 0) {
                toast.success(t('prescriptionSection.removed_all', 'All medicines removed'));
            } else if (failed === results.length) {
                toast.error(t('prescriptionSection.remove_all_failed', 'Failed to remove medicines'));
            } else {
                toast.warn(t('prescriptionSection.remove_all_partial', '{{n}} medicines could not be removed', { n: failed }));
            }
        } finally {
            setRemovingAll(false);
        }
    };

    const commitNotesChange = (row) => {
        const raw = notesDraft[row.prescriptionId];
        setNotesDraft((prev) => {
            if (!(row.prescriptionId in prev)) return prev;
            const next = { ...prev };
            delete next[row.prescriptionId];
            return next;
        });
        if (raw === undefined) return;
        const next = raw.replace(/[<>]/g, '');
        const current = valueOf(row, 'notes') || '';
        if (next === current) return;
        setDraft(row, { notes: next || null });
    };

    const commitDaysChange = (row) => {
        const raw = daysDraft[row.prescriptionId];
        // Clear the typing buffer regardless so the cell reverts to the
        // drafted (or persisted) value on bad input or no-op.
        setDaysDraft((prev) => {
            if (!(row.prescriptionId in prev)) return prev;
            const next = { ...prev };
            delete next[row.prescriptionId];
            return next;
        });
        if (raw === undefined || raw === '') return;
        const n = parseInt(raw, 10);
        if (Number.isNaN(n)) return;
        const clamped = Math.min(MAX_DURATION, Math.max(1, n));
        if (clamped === (valueOf(row, 'durationDays') ?? DEFAULT_DURATION)) return;
        setDraft(row, { durationDays: clamped });
    };

    const colCount = readOnly ? 7 : 8;

    // Hide the whole section when the super-admin Medicines feature is off, and —
    // in read-only (completed) view — when no medicines/products were added
    // (avoids an empty "Treatment Plan – Medicines" table).
    if (!medicinesEnabled) return null;
    if (readOnly && !prescriptionsLoading && prescriptions.length === 0) return null;

    return (
        <div className="flex flex-col w-full gap-3 mb-4 sm:mb-6" data-testid="prescription-section">
            <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                {t('prescriptionSection.heading', 'Treatment Plan – Medicines')}
            </h2>
            {!readOnly && (
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 flex-wrap">
                        {addMode ? (
                            <div ref={pickerRef} className="relative w-64 sm:w-72">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                                <input
                                    data-testid="prescription-search-input"
                                    ref={inputRef}
                                    type="text"
                                    value={addSearch}
                                    onChange={(e) => setAddSearch(e.target.value.replace(/[<>]/g, ''))}
                                    placeholder={t('prescriptionSection.search_placeholder', 'Search by name, brand or purpose')}
                                    className="w-full pl-8 pr-8 py-1.5 rounded-xl border border-[#4ABDBD] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 text-sm bg-white"
                                />
                                <button
                                    data-testid="prescription-search-close"
                                    type="button"
                                    onClick={() => { setAddMode(false); setAddSearch(''); }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 cursor-pointer"
                                    aria-label={t('prescriptionSection.close', 'Close')}
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setAddMode(true)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-xs sm:text-sm font-semibold shadow hover:shadow-md transition cursor-pointer"
                                aria-label={t('prescriptionSection.add_btn', 'Add')}
                                data-testid="prescription-add-btn"
                            >
                                <Plus size={14} /> {t('prescriptionSection.add_btn', 'Add')}
                            </button>
                        )}
                    </div>
                    {prescriptions.length > 0 && (
                        <button
                            type="button"
                            onClick={handleRemoveAll}
                            disabled={removingAll}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-red-200 bg-red-50 text-red-600 text-xs sm:text-sm font-semibold hover:bg-red-100 hover:border-red-300 transition cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                            data-testid="prescription-remove-all-btn"
                        >
                            {removingAll ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Trash2 size={14} />
                            )}
                            {removingAll
                                ? t('prescriptionSection.removing_all', 'Removing...')
                                : t('prescriptionSection.remove_all', 'Remove All')}
                        </button>
                    )}
                </div>
            )}

            <div className="rounded-xl border border-gray-200 overflow-visible">
                <div className="overflow-x-auto overflow-y-visible">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-50 text-gray-600">
                            <tr>
                                <th className="px-3 py-2 text-left font-medium">
                                    {t('prescriptionSection.col_medicine', 'Medicine')}
                                </th>
                                <th className="px-3 py-2 text-center font-medium">
                                    {t('prescriptionSection.col_morning', 'Morning')}
                                </th>
                                <th className="px-3 py-2 text-center font-medium">
                                    {t('prescriptionSection.col_afternoon', 'Afternoon')}
                                </th>
                                <th className="px-3 py-2 text-center font-medium">
                                    {t('prescriptionSection.col_evening', 'Evening')}
                                </th>
                                <th className="px-3 py-2 text-center font-medium">
                                    {t('prescriptionSection.col_night', 'Night')}
                                </th>
                                <th className="px-3 py-2 text-center font-medium">
                                    {t('prescriptionSection.col_food', 'BF / AF')}
                                </th>
                                <th className="px-3 py-2 text-center font-medium">
                                    {t('prescriptionSection.col_days', 'Days')}
                                </th>
                                {!readOnly && <th className="px-3 py-2"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {prescriptionsLoading && (
                                <tr>
                                    <td colSpan={colCount} className="px-3 py-6 text-center text-gray-400 text-sm">
                                        {t('prescriptionSection.loading', 'Loading...')}
                                    </td>
                                </tr>
                            )}
                            {!prescriptionsLoading && prescriptions.length === 0 && readOnly && (
                                <tr>
                                    <td colSpan={colCount} className="px-3 py-6 text-center text-gray-400 text-sm">
                                        {t('prescriptionSection.empty', 'No medicines prescribed yet.')}
                                    </td>
                                </tr>
                            )}
                            {!prescriptionsLoading && prescriptions.length > 0 && (
                                prescriptions.map((row) => {
                                    const isDirty = !!drafts[row.prescriptionId];
                                    const entryMode = (valueOf(row, 'entryMode') === 'T') ? 'T' : 'S';
                                    // Schedule cells span 6 columns (Morning, Afternoon, Evening,
                                    // Night, BF/AF, Days). In T mode we replace them with a single
                                    // colSpan=6 cell containing a resizable textarea.
                                    const textColSpan = 6;
                                    return (
                                        <tr key={row.prescriptionId} data-testid={`prescription-row-${row.prescriptionId}`} className={`border-t border-gray-100 ${isDirty ? 'bg-amber-50/40' : ''}`}>
                                            <td className="px-3 py-3 align-top">
                                                <div className="flex items-start gap-2">
                                                    {row.type === 'product' ? (
                                                        <Package size={14} className="text-amber-500 mt-1" />
                                                    ) : (
                                                        <Pill size={14} className="text-[#4ABDBD] mt-1" />
                                                    )}
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                            <span className="font-medium text-gray-800">{row.medicineName}</span>
                                                            {row.isImportant && (
                                                                <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">
                                                                    <AlertCircle size={10} /> {t('prescriptionSection.important', 'Important')}
                                                                </span>
                                                            )}
                                                            {isDirty && (
                                                                <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700" title={t('prescriptionSection.row_dirty_tooltip', 'Has unsaved changes')}>
                                                                    {t('prescriptionSection.row_dirty', 'Unsaved')}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {(row.brand || row.dose) && (
                                                            <div className="text-xs text-gray-500">
                                                                {row.brand}{row.brand && row.dose ? ' • ' : ''}{row.dose}
                                                            </div>
                                                        )}
                                                        {/* S/T mode toggle — staged in drafts, persisted on Save Changes. */}
                                                        <div className="inline-flex rounded-full bg-gray-100 p-0.5 mt-2" data-testid={`prescription-entry-mode-toggle-${row.prescriptionId}`}>
                                                            {['S', 'T'].map((opt) => (
                                                                <button
                                                                    key={opt}
                                                                    type="button"
                                                                    data-testid={`prescription-entry-mode-${opt}-${row.prescriptionId}`}
                                                                    disabled={readOnly || saving}
                                                                    onClick={() => {
                                                                        if (entryMode === opt) return;
                                                                        setDraft(row, { entryMode: opt });
                                                                    }}
                                                                    className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition cursor-pointer ${
                                                                        entryMode === opt
                                                                            ? 'bg-[#4ABDBD] text-white'
                                                                            : 'text-gray-600'
                                                                    } disabled:cursor-not-allowed`}
                                                                    title={opt === 'S'
                                                                        ? t('prescriptionSection.entry_mode_s_tooltip', 'Schedule — show morning/afternoon/evening/night, BF·AF and days')
                                                                        : t('prescriptionSection.entry_mode_t_tooltip', 'Text content — hide schedule cells and show a free-form note for this row')}
                                                                >
                                                                    {opt}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            {entryMode === 'T' ? (
                                                <td colSpan={textColSpan} className="px-2 py-3">
                                                    <textarea
                                                        value={notesDraft[row.prescriptionId] ?? valueOf(row, 'notes') ?? ''}
                                                        disabled={readOnly || saving}
                                                        onChange={(e) => setNotesDraft((prev) => ({ ...prev, [row.prescriptionId]: e.target.value }))}
                                                        onBlur={() => commitNotesChange(row)}
                                                        placeholder={t('prescriptionSection.text_placeholder', 'Type the instructions for this entry…')}
                                                        rows={2}
                                                        className="w-full text-sm rounded-lg border border-gray-200 px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 disabled:cursor-not-allowed disabled:bg-gray-50 resize-y min-h-[2.5rem]"
                                                        data-testid={`prescription-row-notes-${row.prescriptionId}`}
                                                        aria-label={t('prescriptionSection.entry_mode_t_label', 'Text content')}
                                                    />
                                                </td>
                                            ) : (
                                                <>
                                                    {/* Counters render for products too — products carry the same
                                                        morning/afternoon/evening/night fields and the row is treated
                                                        identically to a medicine in S mode. */}
                                                    {['morningCount', 'afternoonCount', 'eveningCount', 'nightCount'].map((field) => (
                                                        <td key={field} className="px-2 py-3 text-center">
                                                            <Counter
                                                                value={valueOf(row, field) || 0}
                                                                disabled={readOnly || saving}
                                                                onChange={(v) => setDraft(row, { [field]: v })}
                                                            />
                                                        </td>
                                                    ))}
                                                    <td className="px-2 py-3 text-center">
                                                        <div className="inline-flex rounded-full bg-gray-100 p-0.5">
                                                            {['BF', 'AF'].map((opt) => (
                                                                <button
                                                                    key={opt}
                                                                    data-testid={`patient-prescription-food-timing-${opt}-${row.prescriptionId}`}
                                                                    type="button"
                                                                    disabled={readOnly || saving}
                                                                    onClick={() => setDraft(row, { foodTiming: opt })}
                                                                    className={`px-2.5 py-0.5 rounded-full text-xs font-semibold transition cursor-pointer ${
                                                                        valueOf(row, 'foodTiming') === opt
                                                                            ? 'bg-[#4ABDBD] text-white'
                                                                            : 'text-gray-600'
                                                                    } disabled:cursor-not-allowed`}
                                                                    title={opt === 'BF'
                                                                        ? t('prescriptionSection.before_food', 'Before Food')
                                                                        : t('prescriptionSection.after_food', 'After Food')}
                                                                >
                                                                    {opt}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </td>
                                                    <td className="px-2 py-3 text-center">
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            max={MAX_DURATION}
                                                            value={daysDraft[row.prescriptionId] ?? valueOf(row, 'durationDays') ?? DEFAULT_DURATION}
                                                            disabled={readOnly || saving}
                                                            onChange={(e) => setDaysDraft((prev) => ({ ...prev, [row.prescriptionId]: e.target.value }))}
                                                            onBlur={() => commitDaysChange(row)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                                            className="w-14 text-center text-sm font-semibold rounded-lg border border-gray-200 px-1 py-1 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]/30 disabled:cursor-not-allowed disabled:bg-gray-50"
                                                            data-testid={`prescription-row-days-${row.prescriptionId}`}
                                                            aria-label={t('prescriptionSection.col_days', 'Days')}
                                                        />
                                                    </td>
                                                </>
                                            )}
                                            {!readOnly && (
                                                <td className="px-3 py-3 text-center align-top">
                                                    <button
                                                        data-testid={`prescription-remove-button-${row.prescriptionId}`}
                                                        type="button"
                                                        onClick={() => handleRemove(row)}
                                                        className="p-1.5 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 cursor-pointer"
                                                        aria-label={t('prescriptionSection.remove', 'Remove')}
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })
                            )}

                            {!readOnly && !prescriptionsLoading && prescriptions.length === 0 && (
                                <tr>
                                    <td colSpan={colCount} className="px-3 py-6 text-center text-gray-400 text-sm">
                                        {t('prescriptionSection.add_hint_empty', 'Click + to add the first medicine')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Save / Discard bar — row-level edits buffer locally and only
                hit the API when the doctor clicks Save Changes. Hidden in
                readOnly so it never appears in patient views. */}
            {!readOnly && (
                <div className="flex items-center justify-end gap-2 flex-wrap" data-testid="prescription-save-bar">
                    {dirtyCount > 0 && (
                        <span className="text-xs text-amber-700 mr-auto" data-testid="prescription-dirty-count">
                            {t('prescriptionSection.dirty_count', '{{n}} row(s) with unsaved changes', { n: dirtyCount })}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={handleDiscardAll}
                        disabled={saving || dirtyCount === 0}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-gray-200 bg-white text-gray-600 text-xs sm:text-sm font-semibold hover:bg-gray-50 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="prescription-discard-changes-btn"
                    >
                        {t('prescriptionSection.discard_changes', 'Discard Changes')}
                    </button>
                    <button
                        type="button"
                        onClick={handleSaveAll}
                        disabled={saving || dirtyCount === 0}
                        className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] text-white text-xs sm:text-sm font-semibold shadow hover:shadow-md transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="prescription-save-changes-btn"
                    >
                        {saving ? (
                            <>
                                <Loader2 size={14} className="animate-spin" />
                                {t('prescriptionSection.saving', 'Saving…')}
                            </>
                        ) : (
                            t('prescriptionSection.save_changes', 'Save Changes')
                        )}
                    </button>
                </div>
            )}

            {/* Legend — explains the single-letter labels used in the table
                (entry-mode toggle + food-timing pill). */}
            <div
                className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500"
                data-testid="prescription-legend"
            >
                <span><span className="font-semibold text-gray-700">S</span> — {t('prescriptionSection.legend_s', 'Schedule')}</span>
                <span><span className="font-semibold text-gray-700">T</span> — {t('prescriptionSection.legend_t', 'Text content')}</span>
                <span><span className="font-semibold text-gray-700">BF</span> — {t('prescriptionSection.before_food', 'Before Food')}</span>
                <span><span className="font-semibold text-gray-700">AF</span> — {t('prescriptionSection.after_food', 'After Food')}</span>
            </div>

            {addMode && dropdownPos && createPortal(
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'fixed',
                        left: dropdownPos.left,
                        width: dropdownPos.width,
                        top: dropdownPos.top,
                        bottom: dropdownPos.bottom,
                        maxHeight: dropdownPos.maxHeight,
                        zIndex: 1000,
                    }}
                    className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-y-auto"
                >
                    {filteredMaster.length === 0 ? (
                        <p className="text-center text-xs text-gray-500 py-4">
                            {medicines.length > 0 && alreadyAddedIds.size >= medicines.length
                                ? t('prescriptionSection.picker_all_added', 'All medicines already added.')
                                : t('prescriptionSection.picker_empty', 'No medicines found. Add some in your profile first.')}
                        </p>
                    ) : (
                        filteredMaster.map((m) => (
                            <button
                                key={m.medicineId}
                                data-testid={`prescription-medicine-option-${m.medicineId}`}
                                type="button"
                                onClick={() => handlePick(m)}
                                disabled={adding}
                                className="w-full text-left p-2 hover:bg-[#4ABDBD]/5 border-b border-gray-50 last:border-b-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <div className="flex items-center gap-2 flex-wrap">
                                    {m.type === 'product' ? (
                                        <Package size={13} className="text-amber-500 shrink-0" />
                                    ) : (
                                        <Pill size={13} className="text-[#4ABDBD] shrink-0" />
                                    )}
                                    <span className="font-medium text-gray-800 text-sm">{m.name}</span>
                                    {m.brand && (
                                        <span className="text-xs text-gray-500">{m.brand}</span>
                                    )}
                                    {m.type !== 'product' && m.dose && (
                                        <span className="text-xs text-gray-500">• {m.dose}</span>
                                    )}
                                    {m.type !== 'product' && m.isImportant && (
                                        <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">
                                            <AlertCircle size={9} /> {t('prescriptionSection.important', 'Important')}
                                        </span>
                                    )}
                                </div>
                            </button>
                        ))
                    )}
                </div>,
                document.body
            )}
        </div>
    );
}

export default PrescriptionSection;
